"use client";

import { useEffect } from "react";
import { QUEUE_ACTIVE_COOKIE_PREFIX } from "@utils/queue";

/**
 * Keeps an admitted shopper's waiting-room slot(s) alive while they browse/checkout.
 *
 * The status route sets a client-readable `itk_qactive:{groupKey}` cookie per
 * queue group when a visitor is admitted. While any such cookie is present this
 * component pings `/api/queue/heartbeat` on an interval — the endpoint itself
 * refreshes every group the visitor holds a pass for, re-minting each
 * (httpOnly) access pass so none of them lapse mid-purchase.
 *
 * Mounted globally so it covers the event page, basket and checkout without
 * needing to touch each route. No-ops entirely when the visitor has no slot.
 */

// Ping interval. Kept short relative to typical sessionTtlSeconds so a slot is
// always refreshed well before it can expire.
const HEARTBEAT_MS = 30_000;

function hasActiveCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split("; ")
    .some((c) => c.startsWith(QUEUE_ACTIVE_COOKIE_PREFIX) && !c.endsWith("="));
}

export default function QueueHeartbeat() {
  useEffect(() => {
    let cancelled = false;

    async function beat() {
      if (cancelled || !hasActiveCookie()) return;
      try {
        await fetch("/api/queue/heartbeat", {
          method: "POST",
          keepalive: true,
        });
      } catch {
        // Network blip — next tick will retry.
      }
    }

    const id = setInterval(beat, HEARTBEAT_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return null;
}
